import {createAction, props} from '@ngrx/store';
import {buildingStringActionType} from '@appUtil/strings';
import {
  ICalendarRestriction,
  ILabelsCalendar,
  ILabelsRestrictionCard,
} from '@ryndem/pqf-design-system-next-gen';
import {ConfiguracionRestriccionFechasDto} from '@ryndem/api-logistica';
import {
  ILabelsConfigModal,
  ILabelsModalDelete,
  ILabelsModalEdit,
  RestriccionFechasPreviewExtended,
} from '@appModels/delivery-restrictions/delivery-restrictions.models';

const typeReducer = 'DeliveryRestrictionsDetails';
const typeApi = 'DeliveryRestrictionsApi';

/**
 * @description Triggers the initialization of translations for the Delivery Restrictions module.
 * Dispatched once on component load to fetch and set all required i18n labels.
 */
export const INIT_TRANSLATIONS_DELIVERY_RESTRICTIONS = createAction(
  buildingStringActionType(typeApi, 'Init Module Delivery Restrictions'),
);

/**
 * Action to clear all delivery restrictions.
 * Resets the delivery restrictions state to its initial value.
 *
 * @example
 * this.store.dispatch(CLEAR_DELIVERY_RESTRICTIONS());
 */
export const CLEAR_DELIVERY_RESTRICTIONS = createAction(
  buildingStringActionType(typeReducer, 'Init clear delivery restrictions'),
);

/**
 * @description Sets all translation labels for the Delivery Restrictions module in the store.
 * Dispatched after translations are resolved from the i18n service.
 * @prop {string[]} months - List of month name translations.
 * @prop {string[]} days - List of full day name translations.
 * @prop {string[]} daysAbbreviated - List of abbreviated day name translations.
 * @prop {ILabelsRestrictionCard} labelsCards - Labels for the restriction card components.
 * @prop {ILabelsCalendar} labelsCalendar - Labels for the calendar component.
 * @prop {ILabelsConfigModal} labelsConfigModal - Labels for the restriction configuration modal.
 * @prop {ILabelsModalDelete} labelsModalDelete - Labels for the delete confirmation modal.
 * @prop {ILabelsModalEdit} labelsModalEdit - Labels for the edit confirmation modal.
 */
export const SET_TRANSLATION = createAction(
  buildingStringActionType(typeReducer, '[DeliveryRestrictions] Set Translations'),
  props<{
    months: string[];
    days: string[];
    daysAbbreviated: string[];
    labelsCards: ILabelsRestrictionCard;
    labelsCalendar: ILabelsCalendar;
    labelsConfigModal: ILabelsConfigModal;
    labelsModalDelete: ILabelsModalDelete;
    labelsModalEdit: ILabelsModalEdit;
  }>(),
);
/**
 * Action dispatched when the translation loading process fails.
 * Triggered by the `loadTranslations$` effect when an error occurs
 * while retrieving delivery restriction translation keys.
 * @action SET_TRANSLATION_ERROR
 * @param {any} error - The error thrown during the translation loading process.
 */
export const SET_TRANSLATION_ERROR = createAction(
  buildingStringActionType(typeReducer, '[DeliveryRestrictions] Set Translations error'),
  props<{error: any}>(),
);
/**
 * @description Sets the edit mode state for the Delivery Restrictions module.
 * When `true`, enables restriction management actions such as add, edit, and delete.
 * When `false`, reverts the UI to read-only mode.
 * @prop {boolean} isEditMode - `true` to activate edit mode, `false` to deactivate it.
 */
export const SET_EDIT_MODE = createAction(
  buildingStringActionType(typeReducer, 'Set edit mode'),
  props<{isEditMode: boolean}>(),
);
/**
 * Action dispatched to initiate saving the restriction configuration.
 * Triggers the corresponding effect to call the API.
 *
 * @action API
 */
export const GET_RESTRICTIONS_CONFIGURATION_SAVED_LOAD = createAction(
  buildingStringActionType(typeApi, 'Get restriction configuration saved load'),
  props<{typeContext: string; idProvider?: string}>(),
);

/**
 * Action dispatched when the restriction configuration is saved successfully.
 * Can be used to trigger side effects such as closing the modal or refreshing the list.
 *
 * @action API
 */
export const GET_RESTRICTIONS_CONFIGURATION_SAVED_SUCCESS = createAction(
  buildingStringActionType(typeApi, 'Get restriction configuration saved success'),
  props<{listConfiguration: RestriccionFechasPreviewExtended[]}>(),
);

/**
 * Action dispatched when saving the restriction configuration fails.
 * Can be used to handle error states or notify the user.
 *
 * @action API
 */
export const GET_RESTRICTIONS_CONFIGURATION_SAVED_FAILED = createAction(
  buildingStringActionType(typeApi, 'Get restriction configuration saved failed'),
  props<{error: any}>(),
);

/**
 * Initiates the load of a new delivery restriction configuration.
 * @param {ConfiguracionRestriccionFechasDto} listNewConfiguration - Payload containing the new configuration data to load.
 */
export const SET_CONFIGURATION_PREVIEW_LOAD = createAction(
  buildingStringActionType(typeApi, 'Set new configuration load'),
  props<{newConfiguration: RestriccionFechasPreviewExtended}>(),
);

/**
 * Dispatched when the new delivery restriction configuration was loaded successfully.
 * @param {ICalendarRestriction[]} listNewConfigurationPreview - List of calendar restrictions generated from the new configuration.
 */
export const SET_CONFIGURATION_PREVIEW_SUCCESS = createAction(
  buildingStringActionType(typeReducer, 'Set new configuration success'),
  props<{newConfigurationPreview: RestriccionFechasPreviewExtended}>(),
);

/**
 * @description Dispatched when the preview configuration request fails.
 * @prop {any} error - The error returned from the failed request.
 */
export const SET_CONFIGURATION_PREVIEW_FAILED = createAction(
  buildingStringActionType(typeApi, 'Set new configuration failed'),
  props<{error: any}>(),
);

/**
 * @description Updates an existing delivery restriction preview configuration in the store.
 * Replaces the matching configuration identified by `IdConfigRestriction`.
 * @prop {RestriccionFechasPreviewExtended} configurationDR - The updated restriction configuration.
 */
export const SET_CONFIGURATION_PREVIEW_EDIT = createAction(
  buildingStringActionType(typeReducer, 'Set delivery restrictions preview'),
  props<{configurationDR: RestriccionFechasPreviewExtended}>(),
);

/**
 * @description Marks a delivery restriction as pending deletion in the store.
 * If it is a new (unsaved) configuration, removes it from the new config list.
 * If it is a saved configuration, adds its ID to the delete queue.
 * @prop {string} IdConfiguration - The unique identifier of the restriction to delete.
 * @prop {boolean} isNewConfiguration - `true` if the restriction has not been persisted yet.
 */
export const SET_CONFIGURATION_TO_DELETE = createAction(
  buildingStringActionType(typeReducer, 'Set delivery restrictions to deleted'),
  props<{IdConfiguration: string; isNewConfiguration: boolean}>(),
);

/**
 * @description Triggers the save flow for all pending changes:
 * edited restrictions, deleted restrictions, and new configurations.
 */
export const SAVE_ALL_CONFIGURATION_LOAD = createAction(
  buildingStringActionType(typeApi, 'Save all configuration (edit, delete, saved ) load'),
  props<{typeContext: string; idProvider?: string}>(),
);

/**
 * @description Dispatched when all pending configurations have been successfully saved.
 */
export const SAVE_ALL_CONFIGURATION_SUCCESS = createAction(
  buildingStringActionType(typeApi, 'Save all configuration (edit, delete, saved ) success'),
);

/**
 * @description Dispatched when the save flow for all pending configurations fails.
 * @prop {any} error - The error returned from the failed request.
 */
export const SAVE_ALL_CONFIGURATION_FAILED = createAction(
  buildingStringActionType(typeApi, 'Save all configuration (edit, delete, saved ) failed'),
  props<{error: any}>(),
);

/**
 * @description Dispatched when all pending deletion requests complete successfully.
 */
export const CONFIGURATION_DELIVERY_RESTRICTIONS_DELETED_SUCCESS = createAction(
  buildingStringActionType(typeApi, 'Delivery restrictions to deleted success'),
);

/**
 * @description Dispatched when one or more deletion requests fail.
 * @prop {any} error - The error returned from the failed request.
 */
export const CONFIGURATION_DELIVERY_RESTRICTIONS_DELETED_FAILED = createAction(
  buildingStringActionType(typeApi, 'Delivery restrictions to deleted failed'),
  props<{error: any}>(),
);

/**
 * @description Dispatched when all pending edit requests complete successfully.
 */
export const CONFIGURATION_DELIVERY_RESTRICTIONS_EDIT_SUCCESS = createAction(
  buildingStringActionType(typeApi, 'Delivery restrictions edit success'),
);

/**
 * @description Dispatched when one or more edit requests fail.
 * @prop {any} error - The error returned from the failed request.
 */
export const CONFIGURATION_DELIVERY_RESTRICTIONS_EDIT_FAILED = createAction(
  buildingStringActionType(typeApi, 'Delivery restrictions edit failed'),
  props<{error: any}>(),
);
